import { useMemo, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Input, Table, Tooltip } from 'antd';
import { AutoCompleteProps } from 'antd/lib';
import debounce from 'lodash/debounce';

import { InputNumberForm } from '@/components/form/InputNumber';

import styles from './DynamicAerodynamicsTable/styles.module.scss';

interface IAdditionalStaticPressure {
  handleChange: () => void;
  isObserver: boolean;
}

const columnsData = [
  {
    title: 'G0, кг/с',
    title2: 'L0, м³/ч',
    name: 'G0',
    name2: 'L0',
    tooltip: 'Массовый расход воздуха',
    tooltip2: 'Объемный расход воздуха',
    editable: true,
    precision: 0,
  },
  {
    title: 't0, °C',
    name: 't0',
    tooltip: 'Температура воздуха',
    editable: true,
    precision: 0,
  },
  {
    title: 'p0, кг/м³',
    name: 'p0',
    tooltip: 'Плотность воздуха',
    editable: false,
  },
  {
    title: 'a0, мм',
    name: 'a0',
    tooltip: 'Ширина канала',
    editable: true,
    precision: 3,
  },
  {
    title: 'b0, мм',
    name: 'b0',
    tooltip: 'Высота канала',
    editable: true,
    precision: 3,
  },
  {
    title: 'Кэ, мм',
    name: 'Ke',
    tooltip: 'Абсолютная эквивалентная шероховатость поверхности',
    editable: true,
    precision: 1,
  },
  {
    title: 'l, м',
    name: 'l',
    tooltip: 'Длина канала',
    editable: true,
    precision: 1,
  },
  {
    title: 'ξ',
    name: 'E',
    tooltip: 'КМС на участке',
    editable: true,
    precision: 1,
  },
  {
    title: 'S, м²',
    name: 'S',
    tooltip: 'Площадь сечения',
    editable: false,
  },
  {
    title: 'Dэкв, м',
    name: 'Dekv',
    tooltip: 'Эквивалентный диаметр',
    editable: false,
  },
  {
    title: 'v, м/c',
    name: 'v',
    tooltip: 'Скорость воздуха',
    editable: false,
    precision: 2,
  },
  {
    title: 'R, Па/м',
    name: 'R',
    tooltip: 'Потери давления на трение',
    editable: false,
    precision: 2,
  },
  {
    title: 'Pтр, Па',
    name: 'Ptr',
    tooltip: 'Потери давления на трение',
    editable: false,
    precision: 1,
  },
  {
    title: 'Pм, Па',
    name: 'Pm',
    tooltip: 'Потре давления на местные сопротивления',
    editable: false,
    precision: 1,
  },
  {
    title: 'P0, Па',
    name: 'P0',
    tooltip: 'Суммарные потери давления на участке',
    editable: false,
    precision: 1,
  },
];

// Добавочное статическое давление
export const AdditionalStaticPressureTable: React.FC<
  IAdditionalStaticPressure
> = ({ handleChange, isObserver }) => {
  const { control, reset, setValue, getValues, watch } = useFormContext();

  const mode = useMemo(() => watch('mode'), []);

  const { fields, append, remove, insert } = useFieldArray({
    control,
    name: 'additional_static_pressure_table',
  });

  const [autoCompleteOptions] = useState<AutoCompleteProps['options']>([
    {
      key: 'Листовая сталь',
      value: '0.1',
      label: 'Листовая сталь - 0,1мм',
    },
    {
      key: 'Винипласт',
      value: '0.1',
      label: 'Винипласт - 0,1мм',
    },
    {
      key: 'Асбестоцементные плиты или трубы',
      value: '0.11',
      label: 'Асбестоцементные плиты или трубы - 0,11мм',
    },
    {
      key: 'Шлакоалебастровые плиты',
      value: '1',
      label: 'Шлакоалебастровые плиты - 1мм',
    },
    {
      key: 'Шлакобетонные плиты',
      value: '1.5',
      label: 'Шлакобетонные плиты - 1,5мм',
    },
    {
      key: 'Кирпич',
      value: '4',
      label: 'Кирпич - 4мм',
    },
    {
      key: 'Штукатурка (по металлической сетке)',
      value: '10',
      label: 'Штукатурка (по металлической сетке) - 10мм',
    },
  ]);

  const handleAddRow = (index: number) => {
    const { G0, t0, L0 }: any = fields?.[0] || { G0: null };

    const nullValuesObject: any = {
      G0: G0,
      t0: t0,
      L0: L0,
    };
    const newData: any = [...getValues('additional_static_pressure_table')];
    newData.splice(index + 1, 0, {
      ...nullValuesObject,
      i: fields.length + 1,
    });
    setValue('additional_static_pressure_table', newData);

    handleChange();
  };

  const handleRemoveRow = (index: number) => {
    const newData: any = [...getValues('additional_static_pressure_table')];
    newData.splice(index, 1);
    setValue('additional_static_pressure_table', newData);
    handleChange();
  };

  const checkBlurFunction = (
    index: number,
    key: string,
    event: React.FocusEvent<HTMLInputElement>
  ) => {
    const obj: any = fields[index];
    if (obj[key] === null && event?.target?.value === '') return;
    if (obj[key] !== event?.target?.value) {
      handleChange();
    }
  };

  const blurFunction = debounce(checkBlurFunction, 1000);

  const columns = [
    ...columnsData?.map((item) => {
      if (['G0']?.includes(item?.name) && mode === 'м3/ч') {
        return {
          title: (
            <Tooltip placement='top' title={item?.tooltip2}>
              <i className='cursor-default p-2 block'>{item?.title2}</i>
            </Tooltip>
          ),
          dataIndex: item?.name2,
          render: (text: string, record: any, index: number) => (
            <div className='min-w-[120px] p-2'>
              <InputNumberForm
                name={`additional_static_pressure_table[${index}].${item?.name2}`}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                  blurFunction(index, item?.name, event)
                }
                disabled={
                  !item?.editable ||
                  isObserver ||
                  (['G0', 'L0', 't0']?.includes(item?.name) && index !== 0)
                }
                size='middle'
                fixValue={item?.precision}
              />
            </div>
          ),
        };
      } else if (item?.name === 'Ke') {
        return {
          title: (
            <Tooltip placement='top' title={item.tooltip} zIndex={1000}>
              <i className='cursor-default p-2 whitespace-nowrap block'>
                {item.title}
              </i>
            </Tooltip>
          ),
          dataIndex: item.name,
          render: (text: string, record: any, index: number) => (
            <div className='w-[90px] p-2'>
              <Controller
                render={({ field }) => (
                  <AutoComplete
                    options={autoCompleteOptions}
                    style={{ width: 370 }}
                    allowClear={false}
                    onSelect={(value) => {
                      setValue(
                        `additional_static_pressure_table[${index}].${item?.name}`,
                        value
                      );
                      handleChange();
                    }}
                    value={field.value}
                    disabled={isObserver}
                  >
                    <Input
                      {...field}
                      onBlur={(event) => blurFunction(index, item?.name, event)}
                      className='!w-[70px]'
                      onKeyDown={(e) => {
                        if (e.key === ',') {
                          e.preventDefault(); // Prevent the comma from being typed
                        }
                      }}
                      disabled={isObserver}
                    />
                  </AutoComplete>
                )}
                control={control}
                name={`additional_static_pressure_table[${index}].${item?.name}`}
              />
            </div>
          ),
        };
      } else {
        return {
          title: (
            <Tooltip placement='top' title={item?.tooltip}>
              <i className='cursor-default p-2 block'>{item?.title}</i>
            </Tooltip>
          ),
          dataIndex: item?.name,
          render: (text: string, record: any, index: number) => (
            <div className='min-w-[120px] p-2'>
              <InputNumberForm
                name={`additional_static_pressure_table[${index}].${item?.name}`}
                onBlur={(event: React.FocusEvent<HTMLInputElement>) =>
                  blurFunction(index, item?.name, event)
                }
                disabled={
                  !item?.editable ||
                  isObserver ||
                  (['G0', 't0']?.includes(item?.name) && index !== 0)
                }
                size='middle'
                fixValue={item?.precision}
              />
            </div>
          ),
        };
      }
    }),
    {
      title: '',
      dataIndex: 'i',
      render: (text: string, record: any, index: number) => (
        <div className='flex gap-2'>
          <Button
            icon={<PlusOutlined />}
            onClick={() => handleAddRow(index)}
            disabled={isObserver}
          />
          <Button
            disabled={fields?.length === 1 || isObserver}
            icon={<DeleteOutlined />}
            onClick={() => handleRemoveRow(index)}
          />
        </div>
      ),
    },
  ];

  return (
    <div className='relative'>
      <Table
        rowKey={(record) => record?.id}
        dataSource={fields}
        columns={columns}
        pagination={false}
        scroll={{ x: 500 }}
        className={styles.aerotable}
      />
    </div>
  );
};
